import { cutToast } from 'utility/toast';
import { useMutation } from '@apollo/client';
import { IconButton, Stack, TooltipHost } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/UrgencySelector/ConfirmDialog';
import { useCommonStyles } from 'common/styles';
import { TravelerUrgentUpdateInput } from 'common/types/globalTypes';
import React, { useState } from 'react';

import {
  TravelerUrgentUpdate,
  TravelerUrgentUpdateVariables,
} from './__generated__/TravelerUrgentUpdate';
import { useStyles } from './index.styles';
import { Traveler } from '../__generated__/Traveler';

import TRAVELER_URGENCY_UPDATE from './TravelerUrgentUpdate.graphql';
interface UrgencyFolderProps {
  travelerData?: Traveler;
}

export const UrgencyFolder: React.FC<UrgencyFolderProps> = ({
  travelerData,
}) => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const [isVisible, setIsVisible] = useState(false);
  const isUrgent = !Boolean(travelerData?.traveler?._urgencyLevel);
  const urgencyLevelToolTip = isUrgent ? 'Remove Urgency' : 'Elevate to urgent';
  const iconName = isUrgent ? 'FabricFolderFill' : 'FabricFolder';
  const iconStyle = isUrgent ? commonStyles.urgentBox : styles.standardBox;
  const isChecked = !isUrgent;
  const [travelerUrgentUpdate] = useMutation<
    TravelerUrgentUpdate,
    TravelerUrgentUpdateVariables
  >(TRAVELER_URGENCY_UPDATE, { errorPolicy: 'all' });

  const onSubmit = async (requestComment: string | undefined | null) => {
    const updatedUrgencyLevel =
      travelerData?.traveler?._urgencyLevel === 0 ? 1 : 0;
    const { errors } = await travelerUrgentUpdate({
      variables: {
        input: {
          entityId: travelerData?.traveler?.id!,
          rowTimestamp: travelerData?.traveler?._rowTimestamp,
          urgencyLevel: updatedUrgencyLevel,
          explanationComment: requestComment,
        } as TravelerUrgentUpdateInput,
      },
      update(cache, { data }) {
        if (!!travelerData?.traveler?.id) {
          cache.modify({
            id: cache.identify({ ...travelerData.traveler }),
            fields: {
              _urgencyLevel: () => {
                return data?.travelerUrgentUpdate?._newUrgentLevel;
              },
            },
          });
        }
        setIsVisible(false);
      },
    });
    if (errors?.length) cutToast.error(errors[0].message);
    else {
      cutToast.success('Urgency status updated successfully');
    }
  };

  return (
    <Stack>
      <TooltipHost content={urgencyLevelToolTip}>
        <IconButton
          iconProps={{
            iconName: iconName,
          }}
          className={iconStyle}
          onClick={() => setIsVisible(true)}
          styles={{
            rootHovered: { color: 'redDark' },
            rootPressed: { color: 'redDark' },
          }}
        />
      </TooltipHost>
      <ConfirmDialog
        hidden={!isVisible}
        minWidth={500}
        checked={isChecked}
        onConfirm={(requestComment: string | null | undefined) =>
          onSubmit(requestComment)
        }
        onDismiss={() => setIsVisible(false)}
        onCancel={() => setIsVisible(false)}
      />
    </Stack>
  );
};
